import { useEffect, useRef, useState } from 'react';
import { Tag, Wrench, X } from 'shared/icons';
import tagService from '../../services/TagService';

/**
 * TagsColumn
 *
 * Componente para exibir e gerenciar tags de um produto.
 * As tags sao atribuidas baseadas no tipo de identificador (itemId, sku, inventoryId).
 */
const TagsColumn = ({ product, visibleColumns, extractSellerSku }) => {
    const [tags, setTags] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newTagInput, setNewTagInput] = useState('');
    const [allTags, setAllTags] = useState([]);
    const modalRef = useRef(null);
    const inputRef = useRef(null);

    // Identificar quais tipos de identificadores estao visiveis
    const getVisibleIdentifiers = () => {
        const identifiers = [];

        visibleColumns.forEach((column) => {
            if (column.type === 'predefined') {
                if (column.key === 'title' || column.key === 'itemId') {
                    const itemId = product.itemId || product.item_id;
                    if (itemId) {
                        identifiers.push({ type: 'itemId', value: itemId });
                    }
                } else if (column.key === 'sku') {
                    const sku = extractSellerSku(product);
                    if (sku) {
                        identifiers.push({ type: 'sku', value: sku });
                    }
                } else if (column.key === 'inventoryId') {
                    const inventoryId = product.inventoryId;
                    if (inventoryId) {
                        identifiers.push({ type: 'inventoryId', value: inventoryId });
                    }
                }
            }
        });

        return identifiers;
    };

    // Carregar tags baseadas nos identificadores visiveis
    useEffect(() => {
        const identifiers = getVisibleIdentifiers();

        if (identifiers.length === 0) {
            setTags([]);
            return;
        }

        // Obter tags de todos os identificadores visiveis
        const allProductTags = tagService.getTagsForMultiple(identifiers);
        setTags(allProductTags);

        // Carregar todas as tags disponiveis para autocomplete
        const allAvailableTags = tagService.getAllTags();
        setAllTags(allAvailableTags);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, visibleColumns, extractSellerSku]);

    // Fechar modal ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setIsModalOpen(false);
                setNewTagInput('');
            }
        };

        if (isModalOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Focar no input quando abrir
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isModalOpen]);

    // Fechar com ESC
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isModalOpen) {
                setIsModalOpen(false);
                setNewTagInput('');
            }
        };

        if (isModalOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
        };
    }, [isModalOpen]);

    const handleAddTag = () => {
        if (!newTagInput.trim()) {
            return;
        }

        const identifiers = getVisibleIdentifiers();

        // Adicionar tag ao primeiro identificador (que e sempre a primeira coluna - index column)
        // Conforme especificado: "To add a tag, it's always based on the primary index column"
        if (identifiers.length > 0) {
            const firstIdentifier = identifiers[0]; // Primeira coluna = index column
            tagService.addTag(firstIdentifier.type, firstIdentifier.value, newTagInput.trim());

            // Atualizar tags exibidas
            const updatedTags = tagService.getTagsForMultiple(identifiers);
            setTags(updatedTags);

            // Atualizar lista de todas as tags
            const allAvailableTags = tagService.getAllTags();
            setAllTags(allAvailableTags);

            setNewTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        const identifiers = getVisibleIdentifiers();

        // Remover tag de todos os identificadores que a possuem
        identifiers.forEach(({ type, value }) => {
            tagService.removeTag(type, value, tagToRemove);
        });

        // Atualizar tags exibidas
        const updatedTags = tagService.getTagsForMultiple(identifiers);
        setTags(updatedTags);

        // Atualizar lista de todas as tags
        const allAvailableTags = tagService.getAllTags();
        setAllTags(allAvailableTags);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    // Filtrar sugestoes de tags baseado no input
    const filteredSuggestions = allTags
        .filter(
            (tag) =>
                tag.toLowerCase().includes(newTagInput.toLowerCase()) &&
                !tags.some((t) => t.toLowerCase() === tag.toLowerCase()),
        )
        .slice(0, 5);

    const identifiers = getVisibleIdentifiers();
    const firstIdentifier = identifiers.length > 0 ? identifiers[0] : null;

    return (
        <div className="relative">
            <div className="flex items-center gap-1 flex-wrap min-h-[32px] px-2 py-1">
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="p-1 hover:bg-mono-100 dark:hover:bg-mono-800 rounded transition-colors flex-shrink-0"
                    title="Gerenciar tags"
                >
                    <Wrench size={14} className="text-mono-600 dark:text-mono-400" />
                </button>

                {tags.length > 0 ? (
                    tags.slice(0, 3).map((tag, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-md"
                        >
                            {tag}
                        </span>
                    ))
                ) : (
                    <span className="text-xs text-mono-400 dark:text-mono-600">Sem tags</span>
                )}

                {tags.length > 3 && (
                    <span className="text-xs text-mono-500 dark:text-mono-400">+{tags.length - 3}</span>
                )}
            </div>

            {/* Modal de gerenciamento de tags */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
                    <div
                        ref={modalRef}
                        className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl shadow-xl max-w-md w-full p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Tag size={20} className="text-mono-600 dark:text-mono-400" />
                                <h3 className="text-lg font-semibold text-black dark:text-white">Gerenciar Tags</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setNewTagInput('');
                                }}
                                className="p-1 hover:bg-mono-100 dark:hover:bg-mono-800 rounded transition-colors"
                            >
                                <X size={20} className="text-mono-600 dark:text-mono-400" />
                            </button>
                        </div>

                        {firstIdentifier && (
                            <p className="text-sm text-mono-500 dark:text-mono-400 mb-4">
                                Adicionando tags para: <span className="font-medium">{firstIdentifier.value}</span>
                            </p>
                        )}

                        {/* Tags existentes */}
                        {tags.length > 0 && (
                            <div className="mb-4">
                                <p className="text-sm font-medium text-mono-700 dark:text-mono-300 mb-2">
                                    Tags atuais:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-md"
                                        >
                                            {tag}
                                            <button
                                                onClick={() => handleRemoveTag(tag)}
                                                className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded p-0.5"
                                                title="Remover tag"
                                            >
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Input para adicionar nova tag */}
                        <div className="mb-4">
                            <label
                                htmlFor="new-tag-input"
                                className="block text-sm font-medium text-mono-700 dark:text-mono-300 mb-2"
                            >
                                Adicionar tag:
                            </label>
                            <div className="flex gap-2">
                                <input
                                    id="new-tag-input"
                                    ref={inputRef}
                                    type="text"
                                    value={newTagInput}
                                    onChange={(e) => setNewTagInput(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Digite o nome da tag..."
                                    className="flex-1 px-3 py-2 text-sm border border-mono-300 dark:border-mono-700 rounded-lg bg-white dark:bg-mono-900 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={handleAddTag}
                                    disabled={!newTagInput.trim()}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:bg-mono-300 dark:disabled:bg-mono-700 disabled:cursor-not-allowed"
                                >
                                    Adicionar
                                </button>
                            </div>
                        </div>

                        {/* Sugestoes de tags (ocultas por padrao) */}
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-xs text-mono-500 dark:text-mono-400">Sugestoes:</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {filteredSuggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setNewTagInput(suggestion);
                                            setTimeout(() => handleAddTag(), 0);
                                        }}
                                        className="px-2 py-1 text-xs bg-mono-100 dark:bg-mono-800 text-mono-700 dark:text-mono-300 rounded hover:bg-mono-200 dark:hover:bg-mono-700 transition-colors"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TagsColumn;
